import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';
import { NOTIFICATION_TYPES, loadSettings } from '../lib/notifications';

// Podešavanja → Notifikacije: po vrsti obaveštenja, u aplikaciji i e-mail
export default function NotificationSettings() {
  const { user } = useAuth();
  const { lang, tr } = useI18n();
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { loadSettings().then(setSettings); }, []);

  const toggle = async (type, channel) => {
    const current = settings[type];
    const next = { ...current, [channel]: !current[channel] };
    setSettings((s) => ({ ...s, [type]: next }));
    setError('');
    const { error: e } = await supabase.from('notification_settings')
      .upsert({ user_id: user.id, type, in_app: next.in_app, email: next.email }, { onConflict: 'user_id,type' });
    if (e) {
      setSettings((s) => ({ ...s, [type]: current }));
      setError(e.message);
    }
  };

  const Switch = ({ checked, onChange, label }) => (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      style={{ ...styles.switch, ...(checked ? styles.switchOn : {}) }}
    >
      <span style={{ ...styles.knob, ...(checked ? styles.knobOn : {}) }} />
    </button>
  );

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <h1 style={styles.heading}>{tr('Podešavanja obaveštenja', 'Notification settings', 'Настройки уведомлений')}</h1>
      <p style={styles.sub}>
        {tr(
          'Obaveštenja stižu za sve tvoje naloge i prikazuju se na zvoncu gore desno. Izaberi koja želiš da dobijaš.',
          'Notifications cover all your accounts and appear under the bell at the top right. Choose which ones you want.',
          'Уведомления приходят по всем вашим аккаунтам и отображаются под колокольчиком справа вверху. Выберите нужные.',
        )}
      </p>

      <div style={styles.card}>
        <div style={styles.headRow}>
          <span>{tr('Vrsta obaveštenja', 'Notification type', 'Тип уведомления')}</span>
          <span style={styles.col}>{tr('U aplikaciji', 'In app', 'В приложении')}</span>
          <span style={styles.col}>E-mail</span>
        </div>
        {!settings && <div style={styles.loading}>{tr('Učitavanje...', 'Loading...', 'Загрузка...')}</div>}
        {settings && NOTIFICATION_TYPES.map((nt) => {
          const label = nt.label[lang] || nt.label.sr;
          return (
            <div key={nt.type} style={styles.row}>
              <span style={styles.label}>{label}</span>
              <span style={styles.col}><Switch checked={settings[nt.type].in_app} onChange={() => toggle(nt.type, 'in_app')} label={`${label} — ${tr('u aplikaciji', 'in app', 'в приложении')}`} /></span>
              <span style={styles.col}><Switch checked={settings[nt.type].email} onChange={() => toggle(nt.type, 'email')} label={`${label} — e-mail`} /></span>
            </div>
          );
        })}
      </div>
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.note}>
        {tr(
          'E-mail obaveštenja se uključuju uskoro: tvoj izbor se već čuva i važiće čim slanje mejlova proradi.',
          'Email notifications are coming soon: your choice is saved and will apply once email sending is enabled.',
          'Уведомления по e-mail скоро заработают: ваш выбор уже сохранён и вступит в силу, когда отправка писем будет включена.',
        )}
      </div>
    </div>
  );
}

const styles = {
  heading: { fontSize: 22, marginBottom: 6 },
  sub: { fontSize: 14, color: 'var(--ink-500)', margin: '0 0 18px', lineHeight: 1.5 },
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 18, overflow: 'hidden' },
  headRow: { display: 'flex', alignItems: 'center', padding: '14px 18px', fontSize: 13, fontWeight: 800, color: 'var(--ink-900)', borderBottom: '1px solid var(--line)', background: 'var(--paper)' },
  row: { display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--line)' },
  label: { flex: 1, fontSize: 14, color: 'var(--ink-900)', paddingRight: 10 },
  col: { width: 110, display: 'flex', justifyContent: 'center', textAlign: 'center', flexShrink: 0 },
  loading: { padding: 24, textAlign: 'center', color: 'var(--ink-500)', fontSize: 13 },
  switch: { width: 46, height: 26, borderRadius: 13, border: 'none', background: 'var(--line)', position: 'relative', padding: 0, transition: 'background 0.15s' },
  switchOn: { background: 'var(--lavender-700)' },
  knob: { position: 'absolute', top: 3, left: 3, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.15s' },
  knobOn: { left: 23 },
  error: { marginTop: 12, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '8px 12px', fontSize: 13 },
  note: { marginTop: 12, fontSize: 12.5, color: 'var(--ink-500)' },
};
